import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ShieldCheck, Clock, CheckCircle2 } from 'lucide-react';
import heroWide from '../assets/images/vermeil/hero-wide.jpg';
import heroTall from '../assets/images/vermeil/hero-tall.jpg';

const GoldVermeil = () => {
  const ftcConditions = [
    {
      num: '01',
      title: 'Sterling Silver Base',
      desc: 'A base of sterling silver (minimum 92.5% pure silver, marked 925)'
    },
    {
      num: '02',
      title: 'Gold Karat Purity',
      desc: 'A gold layer of at least 10 karat purity'
    },
    {
      num: '03',
      title: 'Gold Layer Thickness',
      desc: 'A gold layer at least 2.5 microns thick'
    }
  ];

  const materialsComparison = [
    {
      title: 'Gold Plated',
      subtitle: 'Standard Plated Jewelry',
      isFocal: false,
      factors: [
        { label: 'Base Metal', value: 'Any metal (often brass or copper)' },
        { label: 'Gold Thickness', value: 'No legal minimum; often 0.175–0.5 microns' },
        { label: 'Gold Purity', value: 'No legal minimum' },
        { label: 'Typical Lifespan', value: 'Weeks to months' },
        { label: 'Hypoallergenic', value: 'Rarely — base metal often reactive' },
        { label: 'Relative Price', value: 'Lowest' }
      ]
    },
    {
      title: 'Gold Vermeil',
      subtitle: 'House of Seya Benchmark',
      isFocal: true,
      badge: 'Legally Defined Standard',
      factors: [
        { label: 'Base Metal', value: 'Sterling silver only' },
        { label: 'Gold Thickness', value: 'Legal minimum 2.5 microns' },
        { label: 'Gold Purity', value: 'Minimum 10 karat (House of Seya: 24K)' },
        { label: 'Typical Lifespan', value: 'Years, with reasonable care' },
        { label: 'Hypoallergenic', value: 'Yes — sterling silver base is skin-friendly' },
        { label: 'Relative Price', value: 'Meaningfully more affordable than solid gold' }
      ]
    },
    {
      title: 'Solid Gold',
      subtitle: 'Solid Precious Metal',
      isFocal: false,
      factors: [
        { label: 'Base Metal', value: 'Gold throughout' },
        { label: 'Gold Thickness', value: 'N/A — solid' },
        { label: 'Gold Purity', value: 'Typically 14K–22K for jewelry' },
        { label: 'Typical Lifespan', value: 'Lifetime' },
        { label: 'Hypoallergenic', value: 'Depends on alloy' },
        { label: 'Relative Price', value: 'Highest' }
      ]
    }
  ];

  const vermeilDurabilityItems = [
    {
      title: 'Is Gold Vermeil Hypoallergenic?',
      icon: ShieldCheck,
      body: "Sterling silver is nickel-free, which makes it one of the most skin-safe base metals available — nickel is the most common cause of metal contact allergies. Because genuine vermeil is built on sterling silver rather than brass or copper, it's significantly less likely to cause irritation, even for people with sensitive skin. This is one of the material's underrated advantages over cheaper gold-plated alternatives."
    },
    {
      title: 'How Long Does Gold Vermeil Last?',
      icon: Clock,
      body: "With reasonable care, well-made gold vermeil — at or above the 2.5-micron standard — can look genuinely brilliant for years of regular wear. Its lifespan depends on three factors: the thickness of the gold layer, the karat purity used, and how it's cared for day to day. Thinner, mislabeled “vermeil” at well below 2.5 microns can start showing base metal within weeks; genuine vermeil, cared for properly, does not."
    }
  ];

  const carePoints = [
    'Put jewelry on after applying makeup, perfume, and hairspray — never before.',
    'Wipe gently with a soft, dry cloth after each wear to remove oils and residue.',
    'Store pieces in a dry, airtight pouch, away from direct sunlight and other jewelry.',
    'Avoid prolonged exposure to chlorine, harsh cleaning chemicals, and abrasive surfaces.',
    'Remove jewelry before high-friction activities like intense exercise or manual work.'
  ];

  const myths = [
    {
      myth: 'Vermeil is just a fancier word for gold plated.',
      reality: 'Vermeil is a legally defined standard — sterling silver base, minimum 10K gold, minimum 2.5 microns thick. Ordinary gold plating has no such minimums and can be applied over any base metal at almost any thickness.'
    },
    {
      myth: 'All vermeil looks and wears the same.',
      reality: 'Quality varies significantly above the legal floor. A piece at 2.5 microns of 24K gold will outperform one that barely meets the minimum with lower-karat gold.'
    },
    {
      myth: "Vermeil isn't real jewelry.",
      reality: 'Vermeil contains genuine gold and genuine sterling silver — both precious metals with real material value, unlike costume jewelry built on base metals.'
    }
  ];

  const faqs = [
    {
      q: 'Can gold vermeil get wet?',
      a: 'Yes, properly made vermeil at or above the 2.5-micron standard can handle everyday water exposure, including showers. Prolonged swimming or exposure to chlorine and salt water should still be avoided to protect the finish long-term.'
    },
    {
      q: 'Will gold vermeil turn my skin green?',
      a: "This is uncommon with genuine vermeil, since the sterling silver base is far less reactive than the brass or copper bases used in cheap gold-plated jewelry. It's more likely to happen with mislabeled “vermeil” that doesn't actually meet the sterling silver requirement."
    },
    {
      q: 'Can gold vermeil be re-plated?',
      a: "Yes. If the gold layer eventually wears thin after years of use, a vermeil piece can typically be re-plated, since the sterling silver base underneath remains fully intact and valuable — unlike gold-plated jewelry over base metal, which usually isn't worth re-plating."
    },
    {
      q: 'Is 24K gold vermeil better than 14K or 18K?',
      a: "24K offers the richest, purest color, since it contains no alloying metals. It's naturally a little softer than lower-karat gold, so extra care around scratches and knocks helps it stay looking its best for longer."
    }
  ];

  return (
    <main className="bg-[#FFFFFF] text-[#1a1a1a] selection:bg-[var(--brand-green-tint-bg)] selection:text-[var(--brand-green)] font-sans min-h-screen">

      {/* 1. HERO — the artwork carries its own title, so no text overlay */}
      <picture>
        <source media="(min-width: 768px)" srcSet={heroWide} />
        <img src={heroTall} alt="The Complete Gold Vermeil Guide" className="block w-full h-auto" />
      </picture>

      <div className="relative z-10 bg-[#FFFFFF]">
        {/* 2. WHAT IS GOLD VERMEIL? */}
      <section className="py-20 lg:py-28 border-b border-[#EEF1E4] bg-[#FFFFFF]">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1a1a1a] font-normal leading-tight">
            What Is Gold Vermeil?
          </h2>
          <div className="w-12 h-px bg-[var(--brand-sage)] mx-auto my-6 opacity-60" />
          <div className="space-y-5 text-sm sm:text-base text-[#5a5346] leading-relaxed font-sans max-w-3xl mx-auto">
            <p>
              Gold vermeil (pronounced “ver-MAY”) is real gold bonded over a sterling silver base — a category distinct from both ordinary gold plating and solid gold jewelry. It isn't a marketing term brands can apply loosely; it's a specific, defined standard that a piece either meets or doesn't.
            </p>
            <p>
              Vermeil dates back to 18th-century France, where gold was hand-applied over silver to create pieces with the richness of gold at a more attainable cost. Today, that same principle holds, just achieved through modern electroplating rather than fire-gilding.
            </p>
          </div>
        </div>
      </section>

      {/* 3. THE REAL DEFINITION OF GOLD VERMEIL (MATCHING HOME CAROUSEL PATTERN) */}
      <section className="py-20 lg:py-28 border-b border-[#EEF1E4] bg-[#FFFFFF]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">

          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1a1a1a] font-normal leading-tight">
              The Real Definition of Gold Vermeil
            </h2>
            <p className="mt-4 text-sm sm:text-base text-[#5a5346] leading-relaxed font-sans">
              For a piece to genuinely qualify as vermeil, it has to meet three specific conditions, based on the FTC's Jewelry Guides (16 CFR Part 23) — the standard most reputable vermeil brands worldwide use as their benchmark, even outside the US:
            </p>
          </div>

          {/* Carousel matching Home CategoryGrid pattern */}
          <div className="flex sm:grid sm:grid-cols-3 gap-4 lg:gap-6 overflow-x-auto sm:overflow-visible snap-x snap-mandatory sm:snap-none scrollbar-hide -mx-6 px-6 sm:mx-0 sm:px-0 items-stretch mb-12">
            {ftcConditions.map((cond) => (
              <div
                key={cond.num}
                className="flex-shrink-0 w-full sm:w-auto snap-center bg-white border border-[#E5E7EB] p-6 sm:p-8 shadow-sm rounded-sm flex flex-col justify-between h-full min-h-[220px] sm:min-h-[180px] hover:border-[var(--brand-sage)] transition-colors duration-300"
              >
                <div>
                  <span className="font-serif text-4xl text-[var(--brand-green-tint-accent)] opacity-40 font-normal block mb-4">
                    {cond.num}
                  </span>
                  <h3 className="font-serif text-xl lg:text-2xl text-[#1a1a1a] font-normal mb-3">
                    {cond.title}
                  </h3>
                  <p className="text-sm text-[#5a5346] leading-relaxed font-sans">
                    {cond.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Condition Summary */}
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="text-sm sm:text-base text-[#5a5346] leading-relaxed font-sans">
              All three conditions have to be true at once. Miss any one — a thinner gold layer, a lower karat, or a base metal other than sterling silver — and the piece isn't legitimately vermeil, whatever the label says.
            </p>
          </div>

          {/* Callout Box: Note on India */}
          <div className="max-w-3xl mx-auto p-8 bg-[var(--brand-green-tint-bg)] border border-[var(--brand-green-border)]">
            <h3 className="font-serif text-lg text-[var(--brand-green)] font-normal mb-2">
              A note on India:
            </h3>
            <p className="text-xs sm:text-sm text-[#5a5346] leading-relaxed font-sans">
              BIS (Bureau of Indian Standards) hallmarking certifies the purity of the silver or gold itself, but India doesn't have a separate regulatory definition specifically for the term “vermeil.” Reputable vermeil brands, including House of Seya, voluntarily hold themselves to the same three-part international standard described above, on top of BIS hallmarking the silver base.
            </p>
          </div>

        </div>
      </section>

      {/* 4. GOLD VERMEIL VS. GOLD PLATED VS. SOLID GOLD */}
      <section className="py-20 lg:py-28 border-b border-[#EEF1E4] bg-[#FFFFFF]">
        <div className="max-w-[1300px] mx-auto px-6 lg:px-12">

          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1a1a1a] font-normal leading-tight">
              Gold Vermeil vs. Gold Plated vs. Solid Gold
            </h2>
          </div>

          {/* 3 Comparison Cards Carousel matching Diamond Education 4Cs Carousel pattern */}
          <div className="grid grid-flow-col auto-cols-[100%] sm:auto-cols-auto sm:grid-flow-row sm:grid-cols-3 gap-4 lg:gap-6 overflow-x-auto sm:overflow-visible snap-x snap-mandatory sm:snap-none scrollbar-hide -mx-6 px-6 sm:mx-0 sm:px-0 items-stretch">
            {materialsComparison.map((mat, idx) => (
              <div
                key={idx}
                className={`w-full sm:w-auto snap-center p-6 sm:p-8 rounded-sm flex flex-col justify-between h-full transition-all duration-300 ${
                  mat.isFocal
                    ? 'bg-[var(--brand-green-tint-bg)]/90 border-2 border-[var(--brand-green)] shadow-md relative lg:-translate-y-2'
                    : 'bg-white border border-[#E5E7EB] shadow-sm hover:border-[var(--brand-sage)]'
                }`}
              >
                <div>
                  {mat.badge && (
                    <span className="inline-block text-[10px] font-mono uppercase tracking-wider bg-[var(--brand-green)] text-white px-2.5 py-1 font-medium mb-4 rounded-sm">
                      {mat.badge}
                    </span>
                  )}
                  <h3
                    className={`font-serif text-2xl sm:text-3xl font-normal mb-1 ${
                      mat.isFocal ? 'text-[var(--brand-green)]' : 'text-[#1a1a1a]'
                    }`}
                  >
                    {mat.title}
                  </h3>
                  <span className="text-xs font-mono text-[#7a6a54] block mb-8 uppercase tracking-wider">
                    {mat.subtitle}
                  </span>

                  {/* Factor Breakdown */}
                  <div className="space-y-6">
                    {mat.factors.map((f, fIdx) => (
                      <div key={fIdx} className="border-b border-[#EEF1E4] pb-4 last:border-0 last:pb-0">
                        <span className="text-[11px] font-mono uppercase tracking-wider text-[#999959] block mb-1">
                          {f.label}
                        </span>
                        <p
                          className={`text-sm font-sans leading-relaxed ${
                            mat.isFocal ? 'text-[#1a1a1a] font-medium' : 'text-[#5a5346]'
                          }`}
                        >
                          {f.value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 5. HOW THICK SHOULD GOLD VERMEIL ACTUALLY BE? */}
      <section className="py-20 lg:py-28 border-b border-[#EEF1E4] bg-[#FFFFFF]">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12">

          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1a1a1a] font-normal leading-tight">
              How Thick Should Gold Vermeil Actually Be?
            </h2>
          </div>

          <div className="space-y-6 text-sm sm:text-base text-[#5a5346] leading-relaxed font-sans mb-12">
            <p>
              2.5 microns is the legal floor — the minimum a piece can measure and still be called vermeil at all. It is not a premium feature; it's the baseline. A meaningful amount of jewelry marketed as “vermeil” online falls below this threshold, meaning it technically shouldn't carry the label.
            </p>
            <p>
              Some brands go beyond the floor, plating at 3 to 5 microns for extra durability. House of Seya's standard is 2.5 microns of 24K gold — meeting the legitimate international definition of vermeil, using the purest gold available, over a BIS hallmarked sterling silver base.
            </p>
          </div>

          {/* Callout Box: What to ask */}
          <div className="p-8 bg-white border border-[#E5E7EB] shadow-sm">
            <h3 className="font-serif text-lg sm:text-xl text-[#1a1a1a] font-normal mb-3">
              What to ask any vermeil brand before buying:
            </h3>
            <p className="text-xs sm:text-sm text-[#5a5346] leading-relaxed font-sans">
              The exact micron thickness, the karat of gold used, and confirmation that the base is genuinely sterling silver, not a silver-colored base metal. A brand that can't answer clearly is worth being cautious of.
            </p>
          </div>

        </div>
      </section>

      {/* 6 & 7. HYPOALLERGENIC + LIFESPAN CAROUSEL */}
      <section className="py-20 lg:py-28 border-b border-[#EEF1E4] bg-[#FFFFFF]">
        <div className="max-w-[1300px] mx-auto px-6 lg:px-12">

          {/* 2-Card Carousel matching Gold Vermeil comparison pattern */}
          <div className="grid grid-flow-col auto-cols-[100%] sm:auto-cols-auto sm:grid-flow-row sm:grid-cols-2 gap-4 lg:gap-6 overflow-x-auto sm:overflow-visible snap-x snap-mandatory sm:snap-none scrollbar-hide -mx-6 px-6 sm:mx-0 sm:px-0 items-stretch">
            {vermeilDurabilityItems.map((item, idx) => (
              <div
                key={idx}
                className="w-full sm:w-auto snap-center bg-white border border-[#E5E7EB] p-6 sm:p-8 shadow-sm rounded-sm flex flex-col justify-between h-full hover:border-[var(--brand-sage)] transition-colors duration-300"
              >
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <item.icon className="text-[var(--brand-green)] flex-shrink-0" size={24} />
                    <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#1a1a1a] font-normal">
                      {item.title}
                    </h2>
                  </div>
                  <p className="text-sm sm:text-base text-[#5a5346] leading-relaxed font-sans">
                    {item.body}
                  </p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 8. CARING FOR YOUR GOLD VERMEIL JEWELRY */}
      <section id="care" className="py-20 lg:py-28 border-b border-[#EEF1E4] bg-[#FFFFFF]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">

          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1a1a1a] font-normal leading-tight">
              Caring for Your Gold Vermeil Jewelry
            </h2>
          </div>

          {/* Care Checklist Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {carePoints.map((point, idx) => (
              <div
                key={idx}
                className="p-6 bg-white border border-[#E5E7EB] shadow-sm flex items-start gap-4 hover:border-[var(--brand-sage)] transition-colors duration-300"
              >
                <CheckCircle2 className="text-[var(--brand-green)] flex-shrink-0 mt-0.5" size={18} />
                <p className="text-xs sm:text-sm text-[#5a5346] leading-relaxed font-sans">
                  {point}
                </p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 9. COMMON MYTHS ABOUT GOLD VERMEIL */}
      <section className="py-20 lg:py-28 border-b border-[#EEF1E4] bg-[#FFFFFF]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">

          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1a1a1a] font-normal leading-tight">
              Common Myths About Gold Vermeil
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-4">
            {myths.map((item, index) => (
              <AccordionItem
                key={index}
                value={`myth-${index}`}
                className="border-b border-[#D8CEB9] bg-transparent transition-colors duration-200"
              >
                <AccordionTrigger className="py-6 font-serif text-base sm:text-lg text-[#1a1a1a] font-normal hover:no-underline text-left">
                  <div className="flex items-start gap-4 pr-4">
                    <span className="font-mono text-xs px-2.5 py-1 bg-rose-100 text-rose-800 font-medium rounded-sm flex-shrink-0 mt-0.5">
                      Myth
                    </span>
                    <span className="leading-snug">
                      "{item.myth}"
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-6 pt-2 text-xs sm:text-sm text-[#5a5346] leading-relaxed font-sans">
                  <div className="flex items-start gap-4 mt-2">
                    <span className="font-mono text-xs px-2.5 py-1 bg-[var(--brand-green-tint-bg)] text-[var(--brand-green)] font-medium rounded-sm flex-shrink-0 mt-0.5">
                      Reality
                    </span>
                    <p className="text-xs sm:text-sm text-[#5a5346] leading-relaxed font-sans">
                      {item.reality}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

        </div>
      </section>

      {/* 10. FREQUENTLY ASKED QUESTIONS */}
      <section className="py-20 lg:py-28 bg-[#FFFFFF]">
        <div className="max-w-[800px] mx-auto px-6 lg:px-10">

          <div className="text-center mb-10">
            <h2 className="font-serif text-2xl lg:text-3xl text-[#1a1a1a]">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`faq-${index}`}
                className="border-b border-[#d8ceb9]"
              >
                <AccordionTrigger className="font-serif text-base lg:text-lg text-[#1a1a1a] py-5 hover:no-underline text-left">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-[14px] text-[#5a5346] leading-relaxed pb-5 font-sans">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

        </div>
      </section>
      </div>

    </main>
  );
};

export default GoldVermeil;
